// Tenantra Inc. - Rent Report View Component (Professional Style)
import { useState } from 'react';
import { PaymentsTab } from '@/components/tenant/PaymentsTab';
import { FileText, ArrowLeft, TrendingUp, TrendingDown, CheckCircle, Clock, AlertTriangle, XCircle, Calendar, Home, User, DollarSign, Shield, AlertCircle, ChevronDown, ChevronUp, MessageSquare, Loader2, Award, Target, BarChart3, Download, Printer, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/services/api';

// Import Official Report components
import { ReportSelectionScreen, OfficialRentReport } from './OfficialRentReport';

// Score category configurations - Professional style
const SCORE_CATEGORIES = {
  excellent: { 
    label: 'Excellent', 
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    description: 'Outstanding rental history'
  },
  good: { 
    label: 'Good', 
    bgColor: 'bg-teal-100',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-200',
    description: 'Reliable tenant record'
  },
  fair: { 
    label: 'Fair', 
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    description: 'Room for improvement'
  },
  poor: { 
    label: 'Poor', 
    bgColor: 'bg-red-100',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
    description: 'Needs significant improvement'
  },
  insufficient_data: { 
    label: 'New Tenant', 
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-500',
    borderColor: 'border-slate-200',
    description: 'Building rental history'
  }
};

// Payment status config - Professional style
const PAYMENT_STATUS = {
  on_time: { label: 'On Time', color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle },
  late: { label: 'Late', color: 'bg-amber-50 text-amber-600 border-amber-200', icon: Clock },
  partial: { label: 'Partial', color: 'bg-orange-50 text-orange-600 border-orange-200', icon: AlertTriangle },
  missed: { label: 'Missed', color: 'bg-red-50 text-red-600 border-red-200', icon: XCircle }
};

// Dispute reasons
const DISPUTE_REASONS = [
  { value: 'not_my_tenancy', label: 'This is not my tenancy' },
  { value: 'incorrect_dates', label: 'Incorrect dates reported' },
  { value: 'incorrect_amount', label: 'Incorrect payment amount' },
  { value: 'damage_not_caused', label: 'Damage was not caused by me' },
  { value: 'payment_was_made', label: 'Payment was made on time' },
  { value: 'other', label: 'Other reason' }
];

// Score Gauge Component - Professional style
function ScoreGauge({ score, category }) {
  const config = SCORE_CATEGORIES[category] || SCORE_CATEGORIES.insufficient_data;
  const rotation = score !== null ? (score / 100) * 180 - 90 : -90;
  
  return (
    <div className="relative w-64 h-32 mx-auto">
      {/* Gauge background */}
      <svg className="w-full h-full" viewBox="0 0 200 100">
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="33%" stopColor="#f59e0b" />
            <stop offset="66%" stopColor="#14b8a6" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        {/* Background arc */}
        <path
          d="M 20 90 A 80 80 0 0 1 180 90"
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="16"
          strokeLinecap="round"
        />
        {/* Colored arc */}
        <path
          d="M 20 90 A 80 80 0 0 1 180 90"
          fill="none"
          stroke="url(#gaugeGradient)"
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray={`${(score || 0) * 2.5} 251`}
        />
      </svg>
      
      {/* Needle */}
      <div 
        className="absolute bottom-0 left-1/2 origin-bottom w-1 h-16 -translate-x-1/2 transition-transform duration-1000"
        style={{ transform: `translateX(-50%) rotate(${rotation}deg)` }}
      >
        <div className="w-3 h-3 rounded-full bg-slate-700 -translate-x-1 -translate-y-1" />
        <div className="w-1 h-14 bg-slate-700 rounded-full" />
      </div>
      
      {/* Score display */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
        <div className="text-4xl font-bold text-slate-900">
          {score !== null ? score : '—'}
        </div>
        <div className={`text-sm font-medium ${config.textColor}`}>{config.label}</div>
      </div>
    </div>
  );
}

// Factor Bar Component - Compact style
function FactorBar({ label, icon: Icon, score, maxScore, percentage, colorClass }) {
  return (
    <div className="p-3 bg-stone-50 rounded-lg border border-stone-100">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-md ${colorClass.bg} flex items-center justify-center`}>
            <Icon className={`w-3 h-3 ${colorClass.text}`} />
          </div>
          <span className="text-stone-800 font-medium text-sm">{label}</span>
        </div>
        <span className="text-stone-800 font-bold text-sm">{score}/{maxScore}</span>
      </div>
      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClass.bar} rounded-full transition-all duration-1000`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Report Entry Card - Compact style
function ReportCard({ report, onDispute }) {
  const [expanded, setExpanded] = useState(false);
  const payment = PAYMENT_STATUS[report.payment_status] || PAYMENT_STATUS.on_time;
  const PaymentIcon = payment.icon;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl sm:rounded-lg overflow-hidden lens">
      <button 
        onClick={() => setExpanded(!expanded)}
        data-testid="report-entry-toggle"
        className="w-full p-3 flex items-center justify-between text-left hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center">
            <FileText className="w-4 h-4 text-stone-500" />
          </div>
          <div>
            <p className="text-sm text-stone-800 font-medium">{report.property_address}</p>
            <p className="text-xs text-stone-500">
              {formatDate(report.period_start)} — {formatDate(report.period_end)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${payment.color}`}>
            <PaymentIcon className="w-3 h-3" />
            {payment.label}
          </span>
          {report.is_disputed && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">
              Disputed
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-stone-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-stone-400" />
          )}
        </div>
      </button>
      
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-stone-200 pt-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2 bg-stone-50 rounded-lg">
              <p className="text-[10px] text-stone-500 mb-0.5">Landlord</p>
              <p className="text-xs text-stone-800">{report.landlord_name}</p>
            </div>
            <div className="p-2 bg-stone-50 rounded-lg">
              <p className="text-[10px] text-stone-500 mb-0.5">Payment</p>
              <p className="text-xs text-stone-800">
                {report.payment_status === 'on_time' ? 'On Time' : 
                 report.days_late ? `${report.days_late} days late` : report.payment_status}
              </p>
            </div>
            <div className="p-2 bg-stone-50 rounded-lg">
              <p className="text-[10px] text-stone-500 mb-0.5">Issues</p>
              <p className={`text-xs ${report.issues_count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {report.issues_count > 0 ? `${report.issues_count} reported` : 'None'}
              </p>
            </div>
          </div>
          
          {!report.is_disputed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDispute(report)}
              data-testid="dispute-entry-btn"
              className="w-full h-8 border-amber-200 text-amber-600 hover:bg-amber-50 text-xs"
            >
              <AlertTriangle className="w-3 h-3 mr-1.5" />
              Dispute This Entry
            </Button>
          )}
          
          {report.is_disputed && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-700 text-xs">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Dispute Status: {report.dispute_status?.replace('_', ' ')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Dispute Modal - Compact style
function WithdrawConsentModal({ tenancy, sessionId, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    try {
      setSubmitting(true);
      await api.post(`/tenant-portal/consent-withdraw/${sessionId}`, { tenancy_id: tenancy.id, reason: reason || undefined });
      toast.success('Reporting consent withdrawn');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not withdraw consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-stone-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="withdraw-consent-modal">
        <h3 className="text-base font-semibold text-stone-900 mb-2">Withdraw reporting consent</h3>
        <p className="text-xs text-stone-600 leading-relaxed mb-1">{tenancy.property_address}</p>
        <p className="text-xs text-stone-600 leading-relaxed mb-4">
          Tenantra will stop reporting this tenancy to credit bureaus from today. Information already reported under your consent is not affected. Your landlord will still be able to keep internal rent records. This cannot be undone from the portal.
        </p>
        <Label htmlFor="withdraw-reason" className="text-xs text-stone-700">Reason (optional)</Label>
        <Textarea id="withdraw-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} className="text-sm border-stone-300 rounded-none mt-1 mb-4" data-testid="withdraw-consent-reason" />
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10 text-xs rounded-none" data-testid="withdraw-consent-cancel">Keep reporting</Button>
          <Button onClick={submit} disabled={submitting} className="flex-1 h-10 text-xs bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="withdraw-consent-confirm">
            {submitting ? 'Withdrawing…' : 'Withdraw consent'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DisputeModal({ report, sessionId, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason || explanation.length < 10) {
      setError('Please select a reason and provide a detailed explanation (at least 10 characters).');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/tenant-portal/disputes/${sessionId}`, {
        report_entry_id: report.id,
        reason: reason,
        explanation: explanation
      });
      
      toast.success('Dispute submitted successfully!');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit dispute.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white border border-stone-200 rounded-lg shadow-xl w-full max-w-md p-5" data-testid="dispute-modal">
        <h3 className="text-lg font-bold text-stone-800 mb-1">Dispute Report Entry</h3>
        <p className="text-stone-500 text-xs mb-4">
          {report.property_address} • {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-stone-700 text-sm">Reason for Dispute *</Label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full mt-1 h-9 px-3 bg-white border border-stone-200 rounded-lg text-stone-800 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              required
            >
              <option value="">Select</option>
              {DISPUTE_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <Label className="text-stone-700 text-sm">Explanation *</Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              className="mt-1 bg-white border-stone-200 text-stone-800 text-sm min-h-[100px] focus:border-teal-500"
              required
              minLength={10}
            />
            <p className="text-[10px] text-stone-500 mt-1">{explanation.length}/10 minimum characters</p>
          </div>
          
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          )}
          
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="dispute-cancel-btn"
              className="flex-1 h-9 border-stone-200 text-stone-700 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm border-0"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Dispute'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Rent Report View
export default function RentReportView({ data, sessionId, onStartOver }) {
  const [tab, setTab] = useState('payments');
  const [disputeReport, setDisputeReport] = useState(null);
  const [withdrawTenancy, setWithdrawTenancy] = useState(null);
  const [reportData, setReportData] = useState(data);
  const [showReportSelection, setShowReportSelection] = useState(false);
  const [showOfficialReport, setShowOfficialReport] = useState(false);
  const [selectedTenancies, setSelectedTenancies] = useState([]);
  
  const score = reportData.score;
  const categoryConfig = SCORE_CATEGORIES[score.category] || SCORE_CATEGORIES.insufficient_data;

  const refreshReport = async () => {
    try {
      const response = await api.get(`/tenant-portal/rent-report/${sessionId}`);
      setReportData(response.data);
    } catch (err) {
      toast.error('Failed to refresh report');
    }
  };

  const handleDisputeSuccess = () => {
    setDisputeReport(null);
    refreshReport();
  };

  const handleGenerateReport = (count) => {
    // Get the selected tenancies (most recent first)
    const selected = reportData.tenancies.slice(0, count);
    setSelectedTenancies(selected);
    setShowReportSelection(false);
    setShowOfficialReport(true);
  };

  // Show Official Report for printing/download
  if (showOfficialReport) {
    return (
      <OfficialRentReport 
        data={reportData}
        selectedTenancies={selectedTenancies}
        onClose={() => setShowOfficialReport(false)}
      />
    );
  }

  // Show Report Selection Screen
  if (showReportSelection) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center p-4">
        <ReportSelectionScreen
          tenancies={reportData.tenancies}
          onGenerate={handleGenerateReport}
          onCancel={() => setShowReportSelection(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Logo size="md" data-testid="report-logo" />
          
          <div className="flex items-center gap-2">
            {tab === 'report' && <>
              <Button variant="outline" size="sm" onClick={() => setShowReportSelection(true)} className="h-8 border-stone-200 bg-white hover:bg-stone-50 text-stone-700 text-xs" aria-label="Print">
                <Printer className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Print</span>
              </Button>
              <Button size="sm" onClick={() => setShowReportSelection(true)} data-testid="download-pdf-btn" className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs border-0" aria-label="Download PDF">
                <Download className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Download PDF</span>
              </Button>
            </>}
            <Button variant="outline" size="sm" onClick={onStartOver} className="h-8 border-stone-200 bg-white hover:bg-stone-50 text-stone-700 text-xs" data-testid="tenant-logout-btn">
              <ArrowLeft className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-20 sm:pt-24 pb-28 sm:pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="sm:hidden text-lg font-semibold text-stone-900 mb-4" data-testid="tenant-greeting">Hi, {(reportData.tenant_name || '').split(' ')[0]}</p>
          <nav className="fixed sm:static bottom-0 inset-x-0 z-40 bg-white/95 sm:bg-transparent backdrop-blur sm:backdrop-blur-none border-t sm:border-t-0 sm:border-b border-stone-200 flex sm:mb-8 pb-[env(safe-area-inset-bottom)] sm:pb-0" role="tablist" data-testid="tenant-tabs">
            {[['payments', 'Pay', Wallet], ['report', 'Report', FileText]].map(([k, l, Icon]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`flex-1 sm:flex-none flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 py-2.5 sm:px-4 text-[11px] sm:text-sm sm:-mb-px sm:border-b-2 transition-colors ${tab === k ? 'text-teal-700 sm:border-teal-600 sm:text-stone-900 font-medium' : 'text-stone-400 sm:border-transparent sm:text-stone-500 hover:text-stone-800'}`} data-testid={`tenant-tab-${k}`}><Icon className="w-5 h-5 sm:hidden" />{k === 'payments' ? <><span className="sm:hidden">Pay</span><span className="hidden sm:inline">Payments</span></> : <><span className="sm:hidden">Report</span><span className="hidden sm:inline">Rent report</span></>}</button>
            ))}
          </nav>
          {tab === 'payments' ? <PaymentsTab sessionId={sessionId} /> : <>
          {/* Header */}
          <h1 className="hidden sm:block text-lg font-semibold text-stone-800 mb-5">{reportData.tenant_name}</h1>

          {/* Score Card */}
          <div className={`border ${categoryConfig.borderColor} rounded-2xl sm:rounded-lg mb-6 overflow-hidden bg-white lens`} data-testid="tenant-score-card">
            <div className={`${categoryConfig.bgColor} px-4 py-2 border-b ${categoryConfig.borderColor}`}>
              <h2 className={`text-sm font-semibold ${categoryConfig.textColor}`}>Tenant Behaviour Score</h2>
            </div>
            <div className="p-5">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="text-center">
                  <ScoreGauge score={score.score} category={score.category} />
                  <p className="text-xs text-stone-500 mt-2">{score.total_reports} report{score.total_reports !== 1 ? 's' : ''}</p>
                </div>
                
                <div className="space-y-3">
                  <FactorBar
                    label="Payment History"
                    icon={DollarSign}
                    score={score.payment_history.score}
                    maxScore={score.payment_history.max_score}
                    percentage={score.payment_history.percentage}
                    colorClass={{ bg: 'bg-emerald-100', text: 'text-emerald-600', bar: 'bg-emerald-500' }}
                  />
                  <FactorBar
                    label="Lease Compliance"
                    icon={FileText}
                    score={score.lease_compliance.score}
                    maxScore={score.lease_compliance.max_score}
                    percentage={score.lease_compliance.percentage}
                    colorClass={{ bg: 'bg-teal-100', text: 'text-teal-600', bar: 'bg-teal-500' }}
                  />
                  <FactorBar
                    label="Property Care"
                    icon={Home}
                    score={score.property_care.score}
                    maxScore={score.property_care.max_score}
                    percentage={score.property_care.percentage}
                    colorClass={{ bg: 'bg-blue-100', text: 'text-blue-600', bar: 'bg-blue-500' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tenancy History */}
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-stone-800 mb-3">Rentals <span className="text-stone-400 font-normal">({reportData.tenancies.length})</span></h2>
            
            {reportData.tenancies.length === 0 ? (
              <div className="text-center py-8 bg-white border border-stone-200 rounded-lg">
                <Home className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No rentals yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {reportData.tenancies.map(tenancy => (
                  <div 
                    key={tenancy.id}
                    className="bg-white border border-stone-200 rounded-2xl sm:rounded-lg p-4 lens"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm text-stone-800 font-medium">{tenancy.property_address}</p>
                        <p className="text-xs text-stone-500">{tenancy.landlord_name}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        tenancy.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                          : 'bg-stone-100 text-stone-500 border border-stone-200'
                      }`}>
                        {tenancy.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(tenancy.move_in_date).toLocaleDateString()}
                        {tenancy.move_out_date && ` - ${new Date(tenancy.move_out_date).toLocaleDateString()}`}
                      </span>
                      <span>{tenancy.reports_count} reports</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      {tenancy.consent_withdrawn ? (
                        <p className="text-[11px] text-stone-500" data-testid={`consent-withdrawn-${tenancy.id}`}>
                          Reporting stopped {tenancy.consent_withdrawn_at ? new Date(tenancy.consent_withdrawn_at).toLocaleDateString() : ''}
                        </p>
                      ) : (
                        <button
                          onClick={() => setWithdrawTenancy(tenancy)}
                          className="text-[11px] text-stone-500 hover:text-red-600 underline underline-offset-2"
                          data-testid={`consent-withdraw-btn-${tenancy.id}`}
                        >
                          Withdraw reporting consent
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Report Entries */}
          <div>
            <h2 className="text-sm font-semibold text-stone-800 mb-3">Months reported <span className="text-stone-400 font-normal">({reportData.reports.length})</span></h2>
            
            {reportData.reports.length === 0 ? (
              <div className="text-center py-8 bg-white border border-stone-200 rounded-lg">
                <FileText className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                <p className="text-sm text-stone-500">Nothing reported yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reportData.reports.map(report => (
                  <ReportCard 
                    key={report.id} 
                    report={report}
                    onDispute={setDisputeReport}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Download/Print CTA */}
          <button onClick={() => setShowReportSelection(true)} className="mt-6 w-full sm:w-auto h-12 sm:h-10 px-5 rounded-xl sm:rounded-md bg-teal-600 hover:bg-teal-700 active:scale-[0.98] transition-[background-color,transform] text-white text-sm font-medium inline-flex items-center justify-center gap-2" data-testid="generate-report-btn">
            <Download className="w-4 h-4" /> Download report
          </button>
          </>}
        </div>
      </main>

      {/* Withdraw consent */}
      {withdrawTenancy && (
        <WithdrawConsentModal
          tenancy={withdrawTenancy}
          sessionId={sessionId}
          onClose={() => setWithdrawTenancy(null)}
          onSuccess={() => { setWithdrawTenancy(null); refreshReport(); }}
        />
      )}

      {/* Dispute Modal */}
      {disputeReport && (
        <DisputeModal
          report={disputeReport}
          sessionId={sessionId}
          onClose={() => setDisputeReport(null)}
          onSuccess={handleDisputeSuccess}
        />
      )}

      {/* Footer */}
      <footer className="hidden sm:block border-t border-stone-200 py-6 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-stone-500 text-xs">
          © {new Date().getFullYear()} Tenantra Inc. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
